export const ThemeStorageKey = 'ui-theme';
export enum Theme {
  Light = 'light',
  Dark = 'dark',
}
export type HorizonTheme = {
  themeName: Theme;
  isHorizonGridVisualRefresh: boolean;
  onChange: (themeName: Theme) => void;

  // Brand accent (now holds the platform sky-blue; token name is legacy)
  teal: string;
  tealActive: string;
  tealBackgroundColor: string;
  tealLightOpacity: string;

  // Count badge (SKYSPECS turquoise) — record-count indicators next to tabs
  countBadgeBackground: string;
  countBadgeText: string;

  // Background and border colors
  activeMenuItemBackgroundColor: string;
  avatarColor: string;
  borderColor: string;
  borderAccentColor: string;
  buttonBackgroundColor: string;
  buttonBorderColor: string;
  carouselBackgroundColor: string;
  contrastColor: string;
  checkboxBackgroundColor: string;
  defaultStatusColor: string;
  disabledBackground: string;
  donutChartGray: string;
  gray: string;
  inputBackgroundColor: string;
  primaryBackgroundColor: string;
  secondaryBackgroundColor: string;
  selectedListItemBackground: string;
  tableAccentColor: string;
  tableBannerBackgroundColor: string;
  tableHeaderButtonColor: string;
  tertiaryBackgroundColor: string;
  alternatingTableRowColor: string;
  tooltipBackground: string;
  tooltipTextColor: string;

  // Sidebar — the navy rail (shared Horizon platform look)
  sidebarBg: string;
  sidebarBorder: string;
  sidebarText: string;
  sidebarTextMuted: string;
  sidebarActiveBg: string;

  // Text colors
  descriptionColor: string;
  disabledTextColor: string;
  headerColor: string;
  labelColor: string;
  noneTextColor: string;
  secondaryTextColor: string;
  textColor: string;

  // Shadow colors
  darkShadow: string;
  lightShadow: string;
  overlay: string;
  noDamageOverlay: string;

  // Damage severity colors
  severityHighColor: string;
  severityMaxColor: string;

  // Measurement tool colors
  darkRed: string;
  darkTeal: string;

  // 🌈
  red: string;
  backgroundRed: string;
  lightRed: string;
  orange: string;
  yellow: string;
  green: string;
  darkGreen: string;
  purple: string;
  lightBlue: string;

  // Map UI colors
  mapControlsBackground: string;
  mapControlsBackgroundDark: string;
  mapControlsTextColor: string;
  mapControlsBorderColor: string;
  mapControlsButtonBackground: string;
  mapControlsIconColor: string;
  mapControlsIconActiveColor: string;
  mapControlsHoverRing: string;
  mapControlsShadowStrong: string;
  mapControlsShadowMedium: string;
  mapControlsShadowLight: string;
  mapPicturePinStroke: string;
  mapThumbnailShadow: string;
  mapPicturePinShadow: string;
  mapLassoActiveBackground: string;
  mapSplitResizerLine: string;
  mapSplitResizerLineHover: string;
  mapOutlineSelected: string;
  mapOutlineUnreviewed: string;
  mapOutlineHover: string;
  mapOutlineLasso: string;
  mapPictureTypeTagRgbText: string;
  mapPictureTypeTagRgbBackground: string;
  mapPictureTypeTagRgbBorder: string;
  mapPictureTypeTagThermalText: string;
  mapPictureTypeTagThermalBackground: string;
  mapPictureTypeTagThermalBorder: string;

  // Plotro colors
  plot: {
    axisColor: string;
    tickColor: string;
    bladeShapeStroke: string;
    bladeShapeFill: string;
    contourStroke: string;
    defaultPointColor: string;
    pointColors: string[];
    opacity: { less: number; default: number; more: number };
    slicedViewFill: string;
  };
  // Temperature Measurement tool colors
  measurementTextBorder: string;
  measurementTextBoxShadow: string;
  measurementTextColor: string;
  measurementReferenceRectColor: string;
  measurementMainRectColor: string;
  measurementPreviewRectColor: string;
  measurementFocusFillColor: string;
};

const PLOTRO_POINT_COLORS = {
  RED: '#f5222d',
  ORANGE: '#faad14',
  BLUE: '#9bcce3',
  YELLOW: '#ffa500',
  GREEN: '#52c41a',
  PURPLE: '#9747ff',
  MAGENTA: '#ff00ff',
};

/**
 * Legacy SkySpecs teal. No longer the antd primary (Horizon now uses the
 * platform sky-blue below); kept as the canonical teal value for reference.
 * In general, all colors should be accessed through the theme as described in
 * `./THEME.md`.
 */
export const SKYSPECS_TEAL = '#24a4ab';

/**
 * Horizon platform brand palette — shared with the horizon-grid app so the two
 * SkySpecs front-ends read as one platform. Phase 1 (color) re-points Horizon's
 * tokens to these: sky-blue is the primary action color, navy is the structural
 * brand color, teal is the secondary accent. (Sidebar navy + status colors land
 * in later phases — see brand-unification notes.)
 */
// Deeper, AA-accessible SkySpecs blue (white text on it and it-on-white both
// clear contrast). horizon-grid's brighter #30AEE4 fails AA; this stays on-brand.
export const HZ_SKYBLUE = '#0f7bb0';
export const HZ_SKYBLUE_HOVER = '#0a6a9a';
export const HZ_NAVY = '#061F32';
export const HZ_TEAL = '#34A4AB';

export const Themes: { [key in Theme]: Omit<HorizonTheme, 'onChange'> } = {
  [Theme.Light]: {
    themeName: Theme.Light,
    isHorizonGridVisualRefresh: true,

    // Brand accent — sky-blue is the Horizon platform action color (was teal)
    teal: HZ_SKYBLUE,
    tealActive: HZ_SKYBLUE_HOVER,
    tealBackgroundColor: 'rgba(15, 123, 176, 0.1)',
    tealLightOpacity: 'rgba(15, 123, 176, 0.2)',

    // Count badge — SKYSPECS turquoise with white text
    countBadgeBackground: HZ_TEAL,
    countBadgeText: '#ffffff',

    // Background and border colors
    activeMenuItemBackgroundColor: 'rgba(15, 123, 176, 0.1)',
    avatarColor: HZ_TEAL,
    borderColor: '#d4dfe8',
    borderAccentColor: 'rgba(6, 31, 50, 0.1)',
    buttonBackgroundColor: '#fff',
    buttonBorderColor: '#c0d0dd',
    carouselBackgroundColor: '#ffffffd9',
    contrastColor: '#061F32',
    checkboxBackgroundColor: 'unset',
    defaultStatusColor: '#7a9bb5',
    disabledBackground: '#eef3f7',
    donutChartGray: '#e6e6e6',
    gray: '#d4dfe8',
    inputBackgroundColor: '#fff',
    primaryBackgroundColor: '#f4f7fa',
    secondaryBackgroundColor: '#fff',
    selectedListItemBackground: 'rgba(15, 123, 176, 0.1)',
    tableAccentColor: '#e6f5f6',
    tableBannerBackgroundColor: 'lightyellow',
    tableHeaderButtonColor: '#7a9bb5',
    tertiaryBackgroundColor: '#fafafa',
    alternatingTableRowColor: '#f8fafc',
    tooltipBackground: '#061F32',
    tooltipTextColor: 'rgba(255, 255, 255, 0.9)',

    // Sidebar — navy rail
    sidebarBg: HZ_NAVY,
    sidebarBorder: '#0c2d46',
    sidebarText: '#c0d0dd',
    sidebarTextMuted: '#7a9bb5',
    sidebarActiveBg: 'rgba(15, 123, 176, 0.15)',

    // Text colors — Horizon navy scale
    descriptionColor: '#396683',
    disabledTextColor: '#b3c2cf',
    headerColor: '#061F32',
    labelColor: '#396683',
    noneTextColor: '#7a9bb5',
    secondaryTextColor: '#396683',
    textColor: '#061F32',

    // Shadow colors
    darkShadow: 'rgba(0, 0, 0, 0.1)',
    lightShadow: 'rgba(0, 0, 0, 0.2)',
    overlay: 'rgba(0,0,0,0.9)',
    noDamageOverlay: 'rgba(0,0,0,0.7)',

    // Damage severity colors
    severityHighColor: '#ffa500',
    severityMaxColor: '#ff0000',

    // Measurement tool colors
    darkRed: 'rgba(198, 21, 21, 1)',
    darkTeal: 'rgba(2, 137, 139, 0.945)',

    // 🌈 — status hues aligned to the Horizon platform palette
    red: '#dc2626',
    backgroundRed: '#fef2f2',
    lightRed: 'rgba(220, 38, 38, 0.6)',
    orange: '#ea580c',
    yellow: '#fadb14',
    green: '#16a34a',
    darkGreen: '#15803d',
    purple: '#9747ff',
    lightBlue: '#9bcce3',

    // Map UI colors
    mapControlsBackground: 'rgba(0, 0, 0, 0.35)',
    mapControlsBackgroundDark: 'rgba(0, 0, 0, 0.8)',
    mapControlsTextColor: 'rgba(255, 255, 255, 0.9)',
    mapControlsBorderColor: 'rgba(255, 255, 255, 0.35)',
    mapControlsButtonBackground: 'rgba(0, 0, 0, 0.9)',
    mapControlsIconColor: '#ffffff',
    mapControlsIconActiveColor: '#000000',
    mapControlsHoverRing: 'rgba(15, 123, 176, 0.25)',
    mapControlsShadowStrong: 'rgba(0, 0, 0, 0.45)',
    mapControlsShadowMedium: 'rgba(0, 0, 0, 0.5)',
    mapControlsShadowLight: 'rgba(0, 0, 0, 0.3)',
    mapPicturePinStroke: '#000000',
    mapThumbnailShadow: 'rgba(0, 0, 0, 0.25)',
    mapPicturePinShadow: 'rgba(0, 0, 0, 0.35)',
    mapLassoActiveBackground: 'rgba(255, 212, 0, 0.2)',
    mapSplitResizerLine: 'rgba(255, 255, 255, 0.15)',
    mapSplitResizerLineHover: 'rgba(255, 255, 255, 0.35)',
    mapOutlineSelected: '#1E5AA8',
    mapOutlineUnreviewed: '#CFCFCF',
    mapOutlineHover: '#FFD400',
    mapOutlineLasso: '#FFD400',
    mapPictureTypeTagRgbText: '#0b3b73',
    mapPictureTypeTagRgbBackground: '#d6ebff',
    mapPictureTypeTagRgbBorder: '#7cb8ff',
    mapPictureTypeTagThermalText: '#6b1b04',
    mapPictureTypeTagThermalBackground: '#ffe1d6',
    mapPictureTypeTagThermalBorder: '#ff9e78',

    // Plotro colors
    plot: {
      axisColor: '#0000008c',
      tickColor: '#0000001a',
      bladeShapeFill: '#f7f7f7',
      bladeShapeStroke: '#0000001f',
      contourStroke: '#0000000d',
      defaultPointColor: '#8c8c8c',
      pointColors: Object.values(PLOTRO_POINT_COLORS),
      opacity: { less: 0.2, default: 0.6, more: 0.8 },
      slicedViewFill: '#CFE2F3',
    },
    // Temperature Measurement tool colors
    measurementTextBorder: 'rgba(255, 255, 255, 0.4)',
    measurementTextBoxShadow: 'rgba(0, 0, 0, 0.35)',
    measurementTextColor: '#ffffff',
    measurementReferenceRectColor: '#39FF14',
    measurementPreviewRectColor: '#3B82F6',
    measurementMainRectColor: '#EF4444',
    measurementFocusFillColor: '#F5E0C5',
  },
  [Theme.Dark]: {
    themeName: Theme.Dark,
    isHorizonGridVisualRefresh: true,

    // Brand accent — sky-blue is the Horizon platform action color (was teal)
    teal: HZ_SKYBLUE,
    tealActive: HZ_SKYBLUE_HOVER,
    tealBackgroundColor: 'rgba(15, 123, 176, 0.15)',
    tealLightOpacity: 'rgba(15, 123, 176, 0.25)',

    // Count badge — SKYSPECS turquoise with white text
    countBadgeBackground: HZ_TEAL,
    countBadgeText: '#ffffff',

    // Background and border colors — Horizon navy surfaces
    activeMenuItemBackgroundColor: 'rgba(15, 123, 176, 0.15)',
    avatarColor: HZ_TEAL,
    borderColor: '#2a5578',
    borderAccentColor: 'rgba(152, 181, 202, 0.15)',
    buttonBackgroundColor: '#103750',
    buttonBorderColor: '#1e4a6b',
    carouselBackgroundColor: 'rgba(0, 0, 0, 0.6)',
    contrastColor: '#fff',
    checkboxBackgroundColor: '#0c2d46',
    defaultStatusColor: '#5e8ba5',
    disabledBackground: '#0c2d46',
    donutChartGray: '#e6e6e6',
    gray: '#5e8ba5',
    inputBackgroundColor: '#0c2d46',
    primaryBackgroundColor: HZ_NAVY,
    secondaryBackgroundColor: '#0c2d46',
    selectedListItemBackground: 'rgba(15, 123, 176, 0.15)',
    tableAccentColor: '#0c3a4a',
    tableBannerBackgroundColor: '#aa8c2b',
    tableHeaderButtonColor: '#5e8ba5',
    tertiaryBackgroundColor: '#123a55',
    alternatingTableRowColor: '#0a2740',
    tooltipBackground: '#103750',
    tooltipTextColor: 'rgba(255, 255, 255, 0.9)',

    // Sidebar — navy rail
    sidebarBg: '#041522',
    sidebarBorder: '#0c2d46',
    sidebarText: '#c0d0dd',
    sidebarTextMuted: '#7a9bb5',
    sidebarActiveBg: 'rgba(15, 123, 176, 0.15)',

    // Text colors — Horizon navy scale (dark)
    descriptionColor: '#98b5ca',
    disabledTextColor: '#5e8ba5',
    headerColor: '#f0f5f9',
    labelColor: '#98b5ca',
    noneTextColor: '#5e8ba5',
    secondaryTextColor: '#98b5ca',
    textColor: '#f0f5f9',

    // Shadow colors
    darkShadow: 'rgba(0, 0, 0, 0.6)',
    lightShadow: 'rgba(0, 0, 0, 0.2)',
    overlay: 'rgba(66, 66, 68, .75)',
    noDamageOverlay: 'rgba(0, 0, 0, 0.7)',

    // Damage severity colors
    severityHighColor: '#ffa500',
    severityMaxColor: '#ff0000',

    // Measurement tool colors
    darkRed: 'rgba(198, 21, 21, 1)',
    darkTeal: 'rgba(2, 137, 139, 0.945)',

    // 🌈 — status hues aligned to the Horizon platform palette (dark)
    red: '#f87171',
    backgroundRed: '#3a1a1a',
    lightRed: 'rgba(248, 113, 113, 0.6)',
    orange: '#fb923c',
    yellow: '#fadb14',
    green: '#4ade80',
    darkGreen: '#16a34a',
    purple: '#b07cff',
    lightBlue: '#2e7ea3',

    // Map UI colors
    mapControlsBackground: 'rgba(0, 0, 0, 0.35)',
    mapControlsBackgroundDark: 'rgba(0, 0, 0, 0.8)',
    mapControlsTextColor: 'rgba(255, 255, 255, 0.9)',
    mapControlsBorderColor: 'rgba(255, 255, 255, 0.35)',
    mapControlsButtonBackground: 'rgba(0, 0, 0, 0.9)',
    mapControlsIconColor: '#ffffff',
    mapControlsIconActiveColor: '#000000',
    mapControlsHoverRing: 'rgba(15, 123, 176, 0.25)',
    mapControlsShadowStrong: 'rgba(0, 0, 0, 0.45)',
    mapControlsShadowMedium: 'rgba(0, 0, 0, 0.5)',
    mapControlsShadowLight: 'rgba(0, 0, 0, 0.3)',
    mapPicturePinStroke: '#000000',
    mapThumbnailShadow: 'rgba(0, 0, 0, 0.25)',
    mapPicturePinShadow: 'rgba(0, 0, 0, 0.35)',
    mapLassoActiveBackground: 'rgba(255, 212, 0, 0.2)',
    mapSplitResizerLine: 'rgba(255, 255, 255, 0.15)',
    mapSplitResizerLineHover: 'rgba(255, 255, 255, 0.35)',
    mapOutlineSelected: '#1E5AA8',
    mapOutlineUnreviewed: '#CFCFCF',
    mapOutlineHover: '#FFD400',
    mapOutlineLasso: '#FFD400',
    mapPictureTypeTagRgbText: '#0b3b73',
    mapPictureTypeTagRgbBackground: '#d6ebff',
    mapPictureTypeTagRgbBorder: '#7cb8ff',
    mapPictureTypeTagThermalText: '#6b1b04',
    mapPictureTypeTagThermalBackground: '#ffe1d6',
    mapPictureTypeTagThermalBorder: '#ff9e78',

    // Plotro colors
    plot: {
      axisColor: '#b2b0b2ff',
      tickColor: '#b2b0b26a',
      bladeShapeFill: '#66666B80',
      bladeShapeStroke: '#a2a0a21f',
      contourStroke: '#a2a0a22d',
      defaultPointColor: '#f5f4f3',
      pointColors: Object.values(PLOTRO_POINT_COLORS),
      opacity: { less: 0.4, default: 0.8, more: 0.99 },
      slicedViewFill: '#CFE2F3',
    },
    // Temperature Measurement tool colors
    measurementTextBorder: 'rgba(255, 255, 255, 0.4)',
    measurementTextBoxShadow: 'rgba(0, 0, 0, 0.35)',
    measurementTextColor: '#ffffff',
    measurementReferenceRectColor: '#39FF14',
    measurementPreviewRectColor: '#3B82F6',
    measurementMainRectColor: '#EF4444',
    measurementFocusFillColor: '#F5E0C5',
  },
};

/**
 * Palette used while the Horizon Grid visual refresh release toggle is disabled.
 * Remove this theme map when `horizon_reskin` is fully released.
 */
export const LegacyThemes: { [key in Theme]: Omit<HorizonTheme, 'onChange'> } = {
  [Theme.Light]: {
    ...Themes[Theme.Light],
    isHorizonGridVisualRefresh: false,
    teal: SKYSPECS_TEAL,
    tealActive: '#44b6b8',
    tealBackgroundColor: 'rgba(36, 164, 171, 0.1)',
    tealLightOpacity: 'rgba(36, 164, 171, 0.2)',
    activeMenuItemBackgroundColor: 'rgba(0,0,0,0.04)',
    avatarColor: '#9ccb3b',
    borderColor: '#e8e8e8',
    borderAccentColor: 'rgba(0, 0, 0, 0.1)',
    buttonBorderColor: '#d9d9d9',
    contrastColor: '#111',
    defaultStatusColor: '#8c8c8c',
    disabledBackground: '#f5f5f5',
    gray: '#dcdfe2',
    primaryBackgroundColor: '#f0f2f5',
    selectedListItemBackground: '#f0f0f0',
    tableAccentColor: '#ddebe9',
    tableHeaderButtonColor: '#bfbfbf',
    alternatingTableRowColor: 'rgb(252, 252, 252)',
    tooltipBackground: 'rgba(0, 0, 0, 0.75)',
    sidebarBg: '#fafafa',
    sidebarBorder: '#e8e8e8',
    sidebarText: 'rgba(0,0,0,0.85)',
    sidebarTextMuted: 'rgba(0, 0, 0, 0.2)',
    sidebarActiveBg: 'rgba(0, 0, 0, 0.1)',
    descriptionColor: 'rgba(0, 0, 0, 0.75)',
    disabledTextColor: '#d9d9d9',
    headerColor: 'rgba(0,0,0,0.85)',
    labelColor: 'rgba(0, 0, 0, 0.55)',
    noneTextColor: '#ccc',
    secondaryTextColor: 'rgba(0, 0, 0, 0.45)',
    textColor: 'rgba(0, 0, 0, 0.65)',
    red: '#f5222d',
    backgroundRed: '#fff2f0',
    lightRed: 'rgba(245, 34, 45, 0.6)',
    orange: '#faad14',
    green: '#52c41a',
    darkGreen: '#158c1a',
    mapControlsHoverRing: 'rgba(36, 164, 171, 0.25)',
  },
  [Theme.Dark]: {
    ...Themes[Theme.Dark],
    isHorizonGridVisualRefresh: false,
    teal: '#44b6b8',
    tealActive: '#66c5c7',
    tealBackgroundColor: 'rgba(36, 164, 171, 0.1)',
    tealLightOpacity: 'rgba(36, 164, 171, 0.2)',
    activeMenuItemBackgroundColor: '#3a3a3c',
    avatarColor: SKYSPECS_TEAL,
    borderColor: '#424244',
    borderAccentColor: 'rgba(0, 0, 0, 0.1)',
    buttonBackgroundColor: '#2a2b2d',
    buttonBorderColor: '#565557',
    checkboxBackgroundColor: '#3b3b3b',
    defaultStatusColor: '#424244',
    disabledBackground: '#2d2d2d',
    gray: '#8c8c8c',
    inputBackgroundColor: '#1e1f21',
    primaryBackgroundColor: '#252628',
    secondaryBackgroundColor: '#1e1f21',
    selectedListItemBackground: '#1f1f1f',
    tableAccentColor: '#232529',
    tableHeaderButtonColor: '#636161',
    tertiaryBackgroundColor: '#2e2e30',
    alternatingTableRowColor: '#232323',
    tooltipBackground: '#424244',
    sidebarBg: '#2e2e30',
    sidebarBorder: '#424244',
    sidebarText: '#f5f4f3',
    sidebarTextMuted: 'rgba(0, 0, 0, 0.2)',
    sidebarActiveBg: '#3a3a3c',
    descriptionColor: '#f5f4f3',
    disabledTextColor: '#8c8c8c',
    headerColor: '#f5f4f3',
    labelColor: '#a2a0a2',
    noneTextColor: '#8c8c8c',
    secondaryTextColor: '#fafafa',
    textColor: '#f5f4f3',
    red: '#d1395a',
    backgroundRed: '#fff2f0',
    lightRed: 'rgba(245, 34, 45, 0.6)',
    orange: '#faad14',
    green: '#158c1a',
    darkGreen: '#085808',
    mapControlsHoverRing: 'rgba(36, 164, 171, 0.25)',
  },
};
